import React from "react";

/**
 * Which of the two things the tag is saying.
 *
 * `new` is the word on a row that has not been opened — pale cyan, with the
 * band of light crossing it. `count` is the rail's tally of them: red, white,
 * and still, because a number that is already the loudest thing on a quiet
 * rail does not also need to move to be found.
 */
export type NewTagTone = "new" | "count";

export interface NewTagProps {
  /**
   * What the tag says — the word "New" on a row, the number of them on the
   * rail. Never an icon: the tag is small, tinted, and has to survive being
   * read at a glance.
   */
  children: React.ReactNode;
  /**
   * Said instead of `children` for a screen reader, where the visible text is
   * a bare number that means nothing on its own — "3 new enquiries" for the
   * rail's badge. Falls back to the text itself.
   */
  label?: string;
  /** Defaults to the word on a row; see `NewTagTone`. */
  tone?: NewTagTone;
  className?: string;
}

/**
 * Fill, ink, and how the pill is padded — which differs because the two tones
 * hold different things. A word wants room either side of it; a single digit
 * wants to come out a disc rather than a squashed oval, so the count is padded
 * to a round minimum and only stretches back into a pill once there is more
 * than one character to hold ("9+").
 */
const TONES: Record<NewTagTone, string> = {
  new: "new-tag-sheen px-2 py-[3px] bg-[#5BD3E1] text-[#007E8C]",
  count: "min-w-[20px] h-[20px] px-1.5 bg-[#FF6158] text-white",
};

/**
 * The flag on something that has not been looked at yet.
 *
 * Two shadows, and they are doing opposite jobs: a soft dark one underneath
 * lifts the tag off whatever it sits on, and a white glow around it separates
 * it from the tinted row it lands in. The band of light crossing the `new`
 * tone is `new-tag-sheen` in `index.css` — the same trick the loading
 * placeholders use, slower and gentler, and dropped entirely for anyone who
 * has asked the system for less animation.
 *
 * The tag is a statement, not a control: nothing is pressed to dismiss it. It
 * goes away because the thing it is on was opened.
 */
export const NewTag: React.FC<NewTagProps> = ({
  children,
  label,
  tone = "new",
  className = "",
}) => (
  <span
    // Not `role="status"`: the tag is part of the row it labels rather than
    // news arriving, and a rail of them announcing themselves on every render
    // is noise. The label is read where the row is.
    className={`inline-flex items-center justify-center flex-shrink-0
      rounded-full text-[11px] font-semibold leading-none
      shadow-[0px_2px_6px_0px_#00000014,0px_0px_10px_0px_#FFFFFF80]
      ${TONES[tone]} ${className}`}
  >
    {label ? <span className="sr-only">{label}</span> : null}
    <span aria-hidden={label ? true : undefined}>{children}</span>
  </span>
);

export default NewTag;
